import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  useInstructorCourses,
  useCreateCourse,
  useUpdateCourse,
  useDeleteCourse,
  useCloneCourse,
  InstructorCourse,
} from "@/hooks/useCourses";
import {
  useInstructorCurricula,
  useCreateCurriculum,
  useUpdateCurriculum,
  useDeleteCurriculum,
  useCloneCurriculum,
  Curriculum,
  InstructorCurriculum,
} from "@/hooks/useCurricula";
import { usePendingReviewCount } from "@/hooks/usePendingReviews";
import { EditCurriculumDialog } from "@/components/instructor/EditCurriculumDialog";
import { CompletionBadgeSelect } from "@/components/badges/CompletionBadgeSelect";
import { setContentBadge, invalidateBadgeQueries } from "@/hooks/useBadges";
import { useHasRole } from "@/hooks/useUserRoles";
import { useQueryClient } from "@tanstack/react-query";
import { ImagePicker } from "@/components/instructor/ImagePicker";
import { CohortTab } from "@/components/instructor/CohortTab";
import { CohortOnlyModal } from "@/components/instructor/CohortOnlyModal";
import { EnrolledUsersDialog } from "@/components/admin/EnrolledUsersDialog";
import { BookOpen, Copy, Edit, Plus, Users, GraduationCap, Trash2, ClipboardCheck, ArrowRight, Lock } from "lucide-react";
import { ContentStatusBadge, ContentStatusControls, type ContentStatus } from "@/components/lms/ContentStatusControls";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function InstructorDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Curriculum state
  const [createCurriculumOpen, setCreateCurriculumOpen] = useState(false);
  const [newCurriculumTitle, setNewCurriculumTitle] = useState("");
  const [newCurriculumDescription, setNewCurriculumDescription] = useState("");
  const [newCurriculumThumbnail, setNewCurriculumThumbnail] = useState<string | null>(null);
  const [deletingCurriculumId, setDeletingCurriculumId] = useState<string | null>(null);
  const [editingCurriculum, setEditingCurriculum] = useState<Curriculum | null>(null);
  
  // Course state
  const [createCourseOpen, setCreateCourseOpen] = useState(false);
  const [newCourseTitle, setNewCourseTitle] = useState("");
  const [newCourseDescription, setNewCourseDescription] = useState("");
  const [newCourseThumbnail, setNewCourseThumbnail] = useState<string | null>(null);
  const [selectedCurriculumId, setSelectedCurriculumId] = useState<string>("");
  const [deletingCourseId, setDeletingCourseId] = useState<string | null>(null);
  
  // Enrolled users dialog state
  const [enrolledDialogOpen, setEnrolledDialogOpen] = useState(false);
  const [enrolledDialogType, setEnrolledDialogType] = useState<"course" | "curriculum">("course");
  const [enrolledDialogId, setEnrolledDialogId] = useState<string>("");
  const [enrolledDialogTitle, setEnrolledDialogTitle] = useState<string>("");

  // Cohort Only modal state
  const [cohortOnlyModal, setCohortOnlyModal] = useState<{
    open: boolean;
    curriculum: { id: string; title: string } | null;
  }>({ open: false, curriculum: null });

  const { data: curricula, isLoading: curriculaLoading } = useInstructorCurricula();
  const { data: courses, isLoading: coursesLoading } = useInstructorCourses();
  const { data: pendingReviewCount } = usePendingReviewCount();
  
  const createCurriculumMutation = useCreateCurriculum();
  const isBadgeAdmin = useHasRole("admin");
  const badgeQueryClient = useQueryClient();
  const [newCurriculumBadgeId, setNewCurriculumBadgeId] = useState<string | null>(null);
  const [newCourseBadgeId, setNewCourseBadgeId] = useState<string | null>(null);
  const updateCurriculumMutation = useUpdateCurriculum();
  const deleteCurriculumMutation = useDeleteCurriculum();
  const cloneCurriculumMutation = useCloneCurriculum();
  const createCourseMutation = useCreateCourse();
  const updateCourseMutation = useUpdateCourse();
  const deleteCourseMutation = useDeleteCourse();
  const cloneCourseMutation = useCloneCourse();

  const handleCreateCurriculum = async () => {
    if (!newCurriculumTitle.trim()) return;

    try {
      const createdCurriculum = await createCurriculumMutation.mutateAsync({
        title: newCurriculumTitle,
        description: newCurriculumDescription || undefined,
        thumbnail_url: newCurriculumThumbnail || undefined,
      });
      if (isBadgeAdmin && newCurriculumBadgeId && createdCurriculum?.id) {
        try {
          await setContentBadge("curriculum", createdCurriculum.id, newCurriculumBadgeId);
          invalidateBadgeQueries(badgeQueryClient);
          badgeQueryClient.invalidateQueries({ queryKey: ["curricula"] });
        } catch (e) {
          toast({ title: "Curriculum created, but the badge was not attached", description: e instanceof Error ? e.message : undefined, variant: "destructive" });
        }
      }
      setNewCurriculumBadgeId(null);
      toast({
        title: "Curriculum created!",
        description: "Now add courses to your curriculum.",
      });
      setCreateCurriculumOpen(false);
      setNewCurriculumTitle("");
      setNewCurriculumDescription("");
      setNewCurriculumThumbnail(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create curriculum. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteCurriculum = async () => {
    if (!deletingCurriculumId) return;
    
    try {
      await deleteCurriculumMutation.mutateAsync(deletingCurriculumId);
      toast({
        title: "Curriculum deleted",
        description: "The curriculum has been removed.",
      });
      setDeletingCurriculumId(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete curriculum.",
        variant: "destructive",
      });
    }
  };

  const handleToggleCurriculumPublish = async (curriculumId: string, isPublished: boolean) => {
    try {
      await updateCurriculumMutation.mutateAsync({
        id: curriculumId,
        is_published: !isPublished,
      });
      toast({
        title: isPublished ? "Curriculum unpublished" : "Curriculum published!",
        description: isPublished
          ? "The curriculum is now hidden from users."
          : "Users can now enroll in this curriculum.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update curriculum status.",
        variant: "destructive",
      });
    }
  };

  const handleSetCurriculumStatus = async (curriculumId: string, next: ContentStatus) => {
    try {
      await updateCurriculumMutation.mutateAsync({ id: curriculumId, status: next } as any);
      toast({
        title:
          next === "published"
            ? "Curriculum published!"
            : next === "preview"
            ? "Sent to preview"
            : "Moved back to draft",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update curriculum status.",
        variant: "destructive",
      });
    }
  };

  const handleToggleCurriculumAccessMode = async (curriculumId: string, currentMode: string, curriculumTitle: string) => {
    const newMode = currentMode === 'cohort_only' ? 'open' : 'cohort_only';
    try {
      await updateCurriculumMutation.mutateAsync({
        id: curriculumId,
        access_mode: newMode,
      });
      toast({
        title: newMode === 'cohort_only' ? "Cohort Only enabled" : "Cohort Only disabled",
        description: newMode === 'cohort_only'
          ? "Enrollment is now restricted to cohort members only."
          : "Anyone can now enroll in this curriculum.",
      });
      if (newMode === 'cohort_only') {
        setCohortOnlyModal({ open: true, curriculum: { id: curriculumId, title: curriculumTitle } });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update access mode.",
        variant: "destructive",
      });
    }
  };

  const handleManageCohorts = (curriculum: { id: string; title: string }) => {
    setCohortOnlyModal({ open: true, curriculum });
  };

  const handleCreateCourse = async () => {
    if (!newCourseTitle.trim() || !selectedCurriculumId) return;

    try {
      const course = await createCourseMutation.mutateAsync({
        title: newCourseTitle,
        description: newCourseDescription || undefined,
        curriculum_id: selectedCurriculumId,
        thumbnail_url: newCourseThumbnail || undefined,
      });
      if (newCourseBadgeId && course?.id) {
        try {
          await setContentBadge("course", course.id, newCourseBadgeId);
          invalidateBadgeQueries(badgeQueryClient);
        } catch (e) {
          toast({ title: "Course created, but the badge was not attached", description: e instanceof Error ? e.message : undefined, variant: "destructive" });
        }
      }
      setNewCourseBadgeId(null);
      toast({
        title: "Course created!",
        description: "Now add modules and content to your course.",
      });
      setCreateCourseOpen(false);
      setNewCourseTitle("");
      setNewCourseDescription("");
      setNewCourseThumbnail(null);
      setSelectedCurriculumId("");
      navigate(`/instructor/courses/${course.id}/edit`);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create course. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleToggleCoursePublish = async (courseId: string, isPublished: boolean) => {
    try {
      await updateCourseMutation.mutateAsync({
        id: courseId,
        is_published: !isPublished,
      });
      toast({
        title: isPublished ? "Course unpublished" : "Course published!",
        description: isPublished
          ? "The course is now hidden from students."
          : "Students can now access this course.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update course status.",
        variant: "destructive",
      });
    }
  };

  const handleSetCourseStatus = async (courseId: string, next: ContentStatus) => {
    try {
      await updateCourseMutation.mutateAsync({ id: courseId, status: next } as any);
      toast({
        title:
          next === "published"
            ? "Course published!"
            : next === "preview"
            ? "Sent to preview"
            : "Moved back to draft",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update course status.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteCourse = async () => {
    if (!deletingCourseId) return;
    
    try {
      await deleteCourseMutation.mutateAsync(deletingCourseId);
      toast({
        title: "Course deleted",
        description: "The course has been removed.",
      });
      setDeletingCourseId(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete course.",
        variant: "destructive",
      });
    }
  };

  const handleCloneCurriculum = async (curriculumId: string) => {
    try {
      await cloneCurriculumMutation.mutateAsync(curriculumId);
      toast({
        title: "Curriculum cloned!",
        description: "A copy has been created as a draft.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to clone curriculum.",
        variant: "destructive",
      });
    }
  };

  const handleCloneCourse = async (courseId: string) => {
    try {
      const clonedCourse = await cloneCourseMutation.mutateAsync(courseId);
      toast({
        title: "Course cloned!",
        description: "A copy has been created as a draft.",
      });
      navigate(`/instructor/courses/${clonedCourse.id}/edit`);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to clone course.",
        variant: "destructive",
      });
    }
  };

  const getCurriculumName = (curriculumId: string | null) => {
    if (!curriculumId) return "No curriculum";
    const curriculum = curricula?.find(c => c.id === curriculumId);
    return curriculum?.title || "Unknown";
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold">Instructor Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Manage your curricula and courses
          </p>
        </div>

        {/* Review Queue Card - Prominent when there are pending submissions */}
        {pendingReviewCount !== undefined && pendingReviewCount > 0 && (
          <Link to="/instructor/review-queue">
            <Card className="border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-orange-100 rounded-xl">
                      <ClipboardCheck className="h-6 w-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">Review Queue</h3>
                      <p className="text-muted-foreground text-sm">
                        {pendingReviewCount} submission{pendingReviewCount !== 1 ? 's' : ''} waiting for your review
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-3xl font-bold text-orange-600">
                      {pendingReviewCount}
                    </span>
                    <Button variant="outline" className="gap-2">
                      View All
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}

        <Tabs defaultValue="curricula" className="space-y-4">
          <TabsList>
            <TabsTrigger value="curricula">Curricula</TabsTrigger>
            <TabsTrigger value="courses">Courses</TabsTrigger>
            <TabsTrigger value="cohorts">Cohorts</TabsTrigger>
          </TabsList>

          <TabsContent value="curricula" className="space-y-4">
            <div className="flex justify-end">
              <Dialog open={createCurriculumOpen} onOpenChange={setCreateCurriculumOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Curriculum
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Curriculum</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="curriculum-title">Curriculum Title</Label>
                      <Input
                        id="curriculum-title"
                        placeholder="Enter curriculum title"
                        value={newCurriculumTitle}
                        onChange={(e) => setNewCurriculumTitle(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="curriculum-description">Description (optional)</Label>
                      <Textarea
                        id="curriculum-description"
                        placeholder="Describe this curriculum"
                        value={newCurriculumDescription}
                        onChange={(e) => setNewCurriculumDescription(e.target.value)}
                        rows={3}
                      />
                    </div>
                    <ImagePicker
                      value={newCurriculumThumbnail}
                      onChange={setNewCurriculumThumbnail}
                      label="Featured Image (optional)"
                    />
                    {isBadgeAdmin && (
                      <CompletionBadgeSelect value={newCurriculumBadgeId} onChange={setNewCurriculumBadgeId} />
                    )}
                    <Button
                      onClick={handleCreateCurriculum}
                      disabled={!newCurriculumTitle.trim() || createCurriculumMutation.isPending}
                      className="w-full"
                    >
                      {createCurriculumMutation.isPending ? "Creating..." : "Create Curriculum"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {curriculaLoading ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-48" />
                ))}
              </div>
            ) : curricula && curricula.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {curricula.map((curriculum) => (
                  <Card key={curriculum.id} className="card-hover">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-lg line-clamp-1">
                          {curriculum.title}
                        </CardTitle>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {curriculum.access_mode === 'cohort_only' && (
                            <div className="flex items-center gap-1">
                              <Badge variant="outline" className="gap-1">
                                <Lock className="h-3 w-3" />
                                Cohort Only
                              </Badge>
                              <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-xs text-muted-foreground"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleManageCohorts({ id: curriculum.id, title: curriculum.title });
                                }}
                              >
                                Manage Cohorts
                              </Button>
                            </div>
                          )}
                          <ContentStatusBadge status={(curriculum as any).status} />

                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {curriculum.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {curriculum.description}
                        </p>
                      )}

                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center">
                          <BookOpen className="h-4 w-4 mr-1" />
                          {curriculum.courses_count || 0} courses
                        </div>
                        <button
                          className="flex items-center hover:text-primary transition-colors cursor-pointer"
                          onClick={() => {
                            setEnrolledDialogType("curriculum");
                            setEnrolledDialogId(curriculum.id);
                            setEnrolledDialogTitle(curriculum.title);
                            setEnrolledDialogOpen(true);
                          }}
                        >
                          <Users className="h-4 w-4 mr-1" />
                          {(curriculum as InstructorCurriculum).enrolled_count || 0} enrolled
                        </button>
                      </div>

                      <div className="space-y-3 pt-2 border-t">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <ContentStatusBadge status={(curriculum as any).status} />
                          <ContentStatusControls
                            status={((curriculum as any).status as ContentStatus) ?? (curriculum.is_published ? "published" : "draft")}
                            disabled={updateCurriculumMutation.isPending}
                            onChange={(next) => handleSetCurriculumStatus(curriculum.id, next)}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={curriculum.access_mode === 'cohort_only'}
                              onCheckedChange={() =>
                                handleToggleCurriculumAccessMode(curriculum.id, curriculum.access_mode || 'open', curriculum.title)
                              }
                              disabled={updateCurriculumMutation.isPending}
                            />
                            <div>
                              <Label className="text-sm">Cohort Only</Label>
                              <p className="text-xs text-muted-foreground">Restrict enrollment to cohort members only</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-1 pt-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleCloneCurriculum(curriculum.id)}
                            disabled={cloneCurriculumMutation.isPending}
                            title="Clone curriculum"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setEditingCurriculum(curriculum)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeletingCurriculumId(curriculum.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <GraduationCap className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">No curricula yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Create your first curriculum to organize courses
                  </p>
                  <Button onClick={() => setCreateCurriculumOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Curriculum
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="courses" className="space-y-4">
            <div className="flex justify-end">
              <Dialog open={createCourseOpen} onOpenChange={setCreateCourseOpen}>
                <DialogTrigger asChild>
                  <Button disabled={!curricula || curricula.length === 0}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Course
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Course</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="course-curriculum">Curriculum</Label>
                      <Select value={selectedCurriculumId} onValueChange={setSelectedCurriculumId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a curriculum" />
                        </SelectTrigger>
                        <SelectContent>
                          {curricula?.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="course-title">Course Title</Label>
                      <Input
                        id="course-title"
                        placeholder="Enter course title"
                        value={newCourseTitle}
                        onChange={(e) => setNewCourseTitle(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="course-description">Description (optional)</Label>
                      <Textarea
                        id="course-description"
                        placeholder="What will students learn?"
                        value={newCourseDescription}
                        onChange={(e) => setNewCourseDescription(e.target.value)}
                        rows={3}
                      />
                    </div>
                    <ImagePicker
                      value={newCourseThumbnail}
                      onChange={setNewCourseThumbnail}
                      label="Featured Image (optional)"
                    />
                    <CompletionBadgeSelect value={newCourseBadgeId} onChange={setNewCourseBadgeId} />
                    <Button
                      onClick={handleCreateCourse}
                      disabled={!newCourseTitle.trim() || !selectedCurriculumId || createCourseMutation.isPending}
                      className="w-full"
                    >
                      {createCourseMutation.isPending ? "Creating..." : "Create Course"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {!curricula || curricula.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <GraduationCap className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Create a curriculum first</h3>
                  <p className="text-muted-foreground mb-4">
                    You need to create a curriculum before adding courses
                  </p>
                  <Button onClick={() => setCreateCurriculumOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Curriculum
                  </Button>
                </CardContent>
              </Card>
            ) : coursesLoading ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-48" />
                ))}
              </div>
            ) : courses && courses.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {courses.map((course) => (
                  <Card key={course.id} className="card-hover">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-lg line-clamp-1">
                          {course.title}
                        </CardTitle>
                        <ContentStatusBadge status={(course as any).status} />

                      </div>
                      <p className="text-xs text-muted-foreground">
                        {getCurriculumName((course as any).curriculum_id)}
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {course.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {course.description}
                        </p>
                      )}

                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <BookOpen className="h-4 w-4" />
                          {(course as InstructorCourse).modules_count || 0} modules
                        </span>
                        <button
                          className="flex items-center gap-1 hover:text-primary transition-colors cursor-pointer"
                          onClick={() => {
                            setEnrolledDialogType("course");
                            setEnrolledDialogId(course.id);
                            setEnrolledDialogTitle(course.title);
                            setEnrolledDialogOpen(true);
                          }}
                        >
                          <Users className="h-4 w-4" />
                          {(course as InstructorCourse).enrolled_count || 0} enrolled
                        </button>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t flex-wrap gap-2">
                        <ContentStatusControls
                          status={((course as any).status as ContentStatus) ?? (course.is_published ? "published" : "draft")}
                          disabled={updateCourseMutation.isPending}
                          onChange={(next) => handleSetCourseStatus(course.id, next)}
                        />

                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleCloneCourse(course.id)}
                            disabled={cloneCourseMutation.isPending}
                            title="Clone course"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/instructor/courses/${course.id}/edit`)}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeletingCourseId(course.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">No courses yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Create your first course to start teaching
                  </p>
                  <Button onClick={() => setCreateCourseOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Course
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="cohorts" className="space-y-4">
            <CohortTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Curriculum Confirmation */}
      <AlertDialog open={!!deletingCurriculumId} onOpenChange={() => setDeletingCurriculumId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Curriculum?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the curriculum. Courses within this curriculum will no longer be associated with any curriculum.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCurriculum}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Course Confirmation */}
      <AlertDialog open={!!deletingCourseId} onOpenChange={() => setDeletingCourseId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Course?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the course and all its modules, content, and surveys. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCourse}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Curriculum Dialog */}
      <EditCurriculumDialog
        open={!!editingCurriculum}
        onOpenChange={(open) => !open && setEditingCurriculum(null)}
        curriculum={editingCurriculum}
      />

      {/* Enrolled Users Dialog */}
      <EnrolledUsersDialog
        open={enrolledDialogOpen}
        onOpenChange={setEnrolledDialogOpen}
        type={enrolledDialogType}
        id={enrolledDialogId}
        title={enrolledDialogTitle}
      />

      {/* Cohort Only Modal */}
      <CohortOnlyModal
        open={cohortOnlyModal.open}
        onOpenChange={(open) => setCohortOnlyModal(prev => ({ ...prev, open }))}
        curriculum={cohortOnlyModal.curriculum || { id: '', title: '' }}
        onDone={() => setCohortOnlyModal({ open: false, curriculum: null })}
      />
    </DashboardLayout>
  );
}
